import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('shopflo-event:checkout-opened', this.#handleCheckoutOpened);
    // Capture on document: Shopflo stops propagation of its checkout click before it reaches the drawer.
    document.addEventListener('click', this.#handleCheckoutClick, true);
  }

  disconnectedCallback() {
    document.removeEventListener('shopflo-event:checkout-opened', this.#handleCheckoutOpened);
    document.removeEventListener('click', this.#handleCheckoutClick, true);
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /**
   * The drawer is a modal <dialog> (top layer), which always paints above Shopflo's checkout
   * overlay no matter its z-index, so close the drawer once Shopflo takes over.
   */
  #handleCheckoutOpened = () => {
    if (this.refs.dialog?.open) this.closeDialog();
  };

  /** @param {Event} event */
  #handleCheckoutClick = (event) => {
    if (event.target instanceof Element && this.contains(event.target) && event.target.closest('[data-flo-action="checkout"]')) {
      this.#handleCheckoutOpened();
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
